import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { api } from "@/lib/api";
import { briefKey } from "@/lib/briefs";
import { getToken } from "@/lib/clerk";

export type Technique = "scene" | "options" | "challenge" | "number" | "case" | "example" | "shorter" | "missing";

export const TECHNIQUES: Array<{ key: Technique; label: string; hint: string }> = [
  { key: "scene", label: "Set the scene", hint: "Who you are, what your team does" },
  { key: "options", label: "Give me options", hint: "Five things what you have could turn into" },
  { key: "challenge", label: "Challenge me", hint: "Read it as the builders, your boss, the user" },
  { key: "number", label: "Put a number on it", hint: "How often, how long, what it costs" },
  { key: "case", label: "Walk me through one case", hint: "The last time it happened, step by step" },
  { key: "example", label: "Show me an example", hint: "A mock-up of the result to react to" },
  { key: "shorter", label: "Shorter, plainer", hint: "Rewrite the boxes plainer" },
  { key: "missing", label: "What's missing?", hint: "Which boxes are weak" },
];

export interface ChatHistory {
  messages: UIMessage[];
  openingChips: string[];
  ai: { enabled: boolean; allowance: { used: number; limit: number; capped: boolean } | null };
}

/**
 * The transcript. An empty id means "not yet": the review pane passes one until
 * the superadmin opens the chat, because that read is audited on the server
 * and must not fire on page load.
 */
export function useChatHistory(briefId: string) {
  return useQuery({
    queryKey: ["chat", briefId],
    queryFn: () => api<ChatHistory>(`/briefs/${briefId}/chat`),
    staleTime: Infinity,
    enabled: briefId !== "",
  });
}

/**
 * The chat, over the AI SDK's UI message stream. Only the last message goes
 * up with each turn: the server keeps the transcript. A `data-brief` part on
 * the way down means the AI wrote into the brief, so the editor refetches.
 */
export function useBriefChat(
  briefId: string,
  history: ChatHistory | undefined,
  onBriefWrite?: (sectionIds: string[]) => void,
) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: briefKey(briefId) });
  // The latest callback without re-creating the chat: the editor uses it to
  // show the box the AI just wrote.
  const onWrite = useRef(onBriefWrite);
  onWrite.current = onBriefWrite;

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `/api/briefs/${briefId}/chat`,
        fetch: async (input, init) => {
          const token = await getToken();
          const headers = new Headers(init?.headers);
          if (token) headers.set("Authorization", `Bearer ${token}`);
          return fetch(input, { ...init, headers });
        },
        prepareSendMessagesRequest: ({ messages, body }) => ({
          body: { message: messages[messages.length - 1], ...(body ?? {}) },
        }),
      }),
    [briefId],
  );

  const chat = useChat<UIMessage>({
    id: briefId,
    transport,
    onFinish: refresh,
    onData: (part) => {
      if (part.type === "data-brief") {
        refresh();
        const ids = (part.data as { sectionIds?: unknown } | undefined)?.sectionIds;
        if (Array.isArray(ids) && ids.length > 0) onWrite.current?.(ids.filter((x): x is string => typeof x === "string"));
      }
    },
  });

  // Seed from the server once; after that the hook owns the list.
  const seeded = useRef(false);
  useEffect(() => {
    if (history && !seeded.current) {
      seeded.current = true;
      chat.setMessages(history.messages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return chat;
}
